// =====================================================
// BUDGET SUMMARY - Resumen y totales del presupuesto
// FIX DESCUENTOS: selector desde catálogo del centro
// =====================================================
import { useState } from 'react';
import { Calculator, AlertTriangle } from 'lucide-react';
import type { DiscountSelect } from '../../api/discounts';
import { formatDiscountValue } from '../../api/discounts';

interface BudgetSummaryProps {
  subtotal: number;
  globalDiscountType: 'PERCENTAGE' | 'FIXED' | null;
  globalDiscountValue: number | null;
  globalDiscountAmount: number;
  total: number;
  globalDiscountId: string | null;
  globalDiscountName: string | null;
  /** Descuentos activos vinculados al centro */
  availableDiscounts: DiscountSelect[];
  onDiscountChange: (
    type: 'PERCENTAGE' | 'FIXED' | null,
    value: number | null,
    discountId: string | null,
    discountName: string | null,
  ) => void;
}

export function BudgetSummary({
  subtotal,
  globalDiscountType,
  globalDiscountValue,
  globalDiscountAmount,
  total,
  globalDiscountId,
  globalDiscountName,
  availableDiscounts,
  onDiscountChange,
}: BudgetSummaryProps) {
  const formatPrice = (price: number) =>
    new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(price);

  const [discountWarning, setDiscountWarning] = useState<{
    discount: DiscountSelect;
    overAmount: number;
  } | null>(null);

  const handleDiscountSelect = (discountId: string) => {
    setDiscountWarning(null);
    if (!discountId) {
      onDiscountChange(null, null, null, null);
      return;
    }
    const discount = availableDiscounts.find(d => d.id === discountId);
    if (!discount) return;

    const discountAmount = discount.type === 'PERCENTAGE'
      ? subtotal * (Number(discount.value) / 100)
      : Number(discount.value);

    if (discountAmount > subtotal) {
      setDiscountWarning({ discount, overAmount: discountAmount - subtotal });
      return;
    }

    onDiscountChange(discount.type, discount.value, discount.id, discount.name);
  };

  const handleConfirmDiscountWarning = () => {
    if (!discountWarning) return;
    const { discount } = discountWarning;
    onDiscountChange(discount.type, discount.value, discount.id, discount.name);
    setDiscountWarning(null);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
        <Calculator className="h-5 w-5 text-gray-400" />
        Resumen
      </h3>

      {/* ── Descuento global del catálogo ──────────── */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Descuento global
        </label>

        {availableDiscounts.length > 0 ? (
          <div className="space-y-2">
            <select
              value={globalDiscountId || ''}
              onChange={(e) => handleDiscountSelect(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Sin descuento global</option>
              {availableDiscounts.map(d => (
                <option key={d.id} value={d.id}>
                  {d.name} — {formatDiscountValue(d.type, d.value)}
                </option>
              ))}
            </select>
            {discountWarning && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                  <span className="text-amber-800">
                    El descuento <strong>{discountWarning.discount.name}</strong> supera el subtotal
                    en <strong>{new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(discountWarning.overAmount)}</strong>.
                    El total del presupuesto quedará en <strong>0,00 €</strong>.
                  </span>
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setDiscountWarning(null)}
                    className="px-3 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleConfirmDiscountWarning}
                    className="px-3 py-1 text-xs font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
                  >
                    Aplicar de todas formas
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-400 italic">
            No hay descuentos configurados para este centro
          </p>
        )}
      </div>

      {/* ── Totales ────────────────────────────────── */}
      <div className="space-y-2 pt-4 border-t border-gray-100">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Subtotal:</span>
          <span className="font-medium text-gray-900">{formatPrice(subtotal)}</span>
        </div>

        {globalDiscountAmount > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">
              {globalDiscountName
                ? `${globalDiscountName} (${formatDiscountValue(globalDiscountType!, globalDiscountValue!)})`
                : globalDiscountType === 'PERCENTAGE' && globalDiscountValue
                ? `Descuento (${globalDiscountValue}%)`
                : 'Descuento'}:
            </span>
            <span className="text-green-600 font-medium">
              -{formatPrice(globalDiscountAmount)}
            </span>
          </div>
        )}

        <div className="flex justify-between items-baseline pt-3 border-t border-gray-200">
          <span className="font-semibold text-gray-700">Total:</span>
          <span className="text-2xl font-bold text-blue-600">{formatPrice(total)}</span>
        </div>
      </div>
    </div>
  );
}
